import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Sparkles, Activity, Files, Contact as ContactIcon, NotebookPen, LayoutList, MessagesSquare, BadgeDollarSign, Banknote, Target, Send, Lock } from "lucide-react";
import "../../../../styles/documents/modals/text-document-modal.css";
import "../../../../styles/kanbans/modals/edit-job-modal.css";
import EditTab from "./job-tabs/EditTab";
import AIToolsTab from "./job-tabs/AIToolsTab";
import ActivitiesTab from "./job-tabs/ActivitiesTab";
import NotesTab from "./job-tabs/NotesTab";
import ContactsTab from "./job-tabs/ContactsTab";
import DocumentsTab from "./job-tabs/DocumentsTab";
import TimelineTab from "./job-tabs/TimelineTab";
import NewActivityModal, { ActivityData } from "../activities/modals/NewActivityModal";
import ContactsModal, { Contact } from "../../contacts/ContactsModal";
import NewDocumentModal from "../../documents/modals/new/NewDocumentModal";
import NewTextDocumentModal from "../../documents/modals/new/options/NewTextDocumentModal";
import NewPDFDocumentModal, { PdfDocData as NewPdfDocData } from "../../documents/modals/new/options/NewPDFDocumentModal";
import { TextDocData } from "../../documents/modals/TextDocumentModal";
import api, { getUserUid } from "../../../../utils/api";
import { API_ENDPOINTS } from "../../../../config";

export interface EditJobModalProps {
  open: boolean;
  onClose: () => void;
  jobId?: string;
  boardId?: string;
  jobTitle?: string;
  companyName?: string;
  currentLane?: string;
  availableLanes?: Array<{ id: string; title: string; icon: string }>;
  onMoveJob?: (laneId: string) => void;
  onColorChange?: (jobId: string, color: string | null) => void;
  defaultLeftTab?: LeftTabKey;
}

type TabKey = 'shortcuts' | 'timeline';
export type LeftTabKey = 'edit' | 'ai-tools' | 'activities' | 'notes' | 'contacts' | 'documents';

interface JobData {
  uid: string;
  job_title: string;
  company_name: string;
  deadline_date?: string | null;
  vacancy_url?: string | null;
  salary?: string | null;
  salary_currency?: string | null;
  color?: string | null;
  description_text?: string | null;
}

const DEFAULT_COLOR = '#D1D5DB';

export default function EditJobModal({
  open,
  onClose,
  jobId = '',
  boardId = '',
  jobTitle = '',
  companyName = '',
  currentLane = 'w',
  availableLanes = [],
  onMoveJob,
  onColorChange,
  defaultLeftTab = 'edit',
}: EditJobModalProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('shortcuts');
  const [activeLeftTab, setActiveLeftTab] = useState<LeftTabKey>(defaultLeftTab);
  const [showMoveDropdown, setShowMoveDropdown] = useState(false);
  const moveWrapperRef = useRef<HTMLDivElement>(null);
  const [jobData, setJobData] = useState<JobData | null>(null);
  const [loadingJobData, setLoadingJobData] = useState(false);
  const [newActivityModalOpen, setNewActivityModalOpen] = useState(false);
  const [allowedCategories, setAllowedCategories] = useState<string[] | undefined>(undefined);
  const [focusNotesEditor, setFocusNotesEditor] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newDocModalOpen, setNewDocModalOpen] = useState(false);
  const [newTextDocModalOpen, setNewTextDocModalOpen] = useState(false);
  const [newPdfDocModalOpen, setNewPdfDocModalOpen] = useState(false);
  const [highlightEntityUid, setHighlightEntityUid] = useState<string | null>(null);

  const handleTimelineNavigate = (entityType: string, entityUid: string | null, leftTab: LeftTabKey) => {
    setActiveLeftTab(leftTab);
    setActiveTab('shortcuts');
    if (entityUid) {
      setHighlightEntityUid(entityUid);
      setTimeout(() => setHighlightEntityUid(null), 3500);
    }
  };

  const renderLaneIcon = (iconKey: string, title: string) => {
    const key = (iconKey || "").toLowerCase();
    const titleUpper = (title || "").toUpperCase();

    if (key === "wishlist" || titleUpper.includes("WISH")) {
      return <LayoutList size={14} />;
    }
    if (key === "applied" || titleUpper.includes("APPLY")) {
      return <Send size={14} />;
    }
    if (key === "interview" || titleUpper.includes("INTERVIEW")) {
      return <MessagesSquare size={14} />;
    }
    if (key === "offer" || titleUpper.includes("OFFER")) {
      return <BadgeDollarSign size={14} />;
    }
    if (key === "rejected" || titleUpper.includes("REJECT")) {
      return <Banknote size={14} />;
    }
    return <Target size={14} />;
  };

  const fetchJobData = async () => {
    if (!open || !jobId || !boardId) {
      return;
    }

    try {
      setLoadingJobData(true);
      const jobsData = await api.get<any[]>(API_ENDPOINTS.jobs.listByKanban(boardId));
      const job = jobsData.find(j => j.uid === jobId);
      
      if (job) {
        setJobData({
          uid: job.uid,
          job_title: job.job_title || '',
          company_name: job.company_name || '',
          deadline_date: job.deadline_date || null,
          vacancy_url: job.vacancy_url || null,
          salary: job.salary || null,
          salary_currency: job.salary_currency || null,
          color: job.color || null,
          description_text: job.description_text || null,
        });
      }
    } catch (error) {

    } finally {
      setLoadingJobData(false);
    }
  };

  useEffect(() => {
    if (jobData && jobData.uid !== jobId) {
      setJobData(null);
    }
    fetchJobData();
  }, [open, jobId, boardId]);

  useEffect(() => {
    if (open && defaultLeftTab) {
      setActiveLeftTab(defaultLeftTab);
    }
  }, [open, defaultLeftTab]);

  useEffect(() => {
    if (open && jobId && boardId) {
      navigate(`/track/board/${boardId}/job/${jobId}`, { replace: true });
    }
  }, [open, jobId, boardId, navigate]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [open]);

  const handleClose = () => {
    if (boardId) {
      navigate(`/track/board/${boardId}`, { replace: true });
    }
    onClose();
  };

  useEffect(() => {
    if (!showMoveDropdown) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!moveWrapperRef.current?.contains(event.target as Node)) {
        setShowMoveDropdown(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowMoveDropdown(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMoveDropdown]);

  const handleMoveToLane = (laneId: string) => {
    if (laneId !== currentLane && onMoveJob) {
      onMoveJob(laneId);
      setShowMoveDropdown(false);
    }
  };

  const handleLogApplication = () => {
    setAllowedCategories(['Apply']);
    setNewActivityModalOpen(true);
  };

  const handleLogInterview = () => {
    setAllowedCategories(['Phone Screen', 'Phone Interview', 'On Site Interview']);
    setNewActivityModalOpen(true);
  };

  const handleLogOfferReceived = () => {
    setAllowedCategories(['Offer Received']);
    setNewActivityModalOpen(true);
  };

  const handleLogOfferAccepted = () => {
    setAllowedCategories(['Accept Offer', 'Decline Offer']);
    setNewActivityModalOpen(true);
  };

  const handleLogOtherActivity = () => {
    const usedCategories = [
      'Apply',
      'Phone Screen',
      'Phone Interview',
      'On Site Interview',
      'Offer Received',
      'Accept Offer',
      'Decline Offer'
    ];
    
    const allCategories = [
      'Prep for Interview',
      'Send Thank You',
      'Send Availability',
      'Apply',
      'Follow Up',
      'Prep Cover Letter',
      'Prep Resume',
      'Reach Out',
      'Phone Screen',
      'Phone Interview',
      'On Site Interview',
      'Offer Received',
      'Accept Offer',
      'Decline Offer',
      'Rejected',
      'Email',
      'Meeting',
      'Phone Call',
      'Get Reference',
      'Assignment',
      'Networking Event',
      'Other',
      'Application Withdrawn'
    ];
    
    const otherCategories = allCategories.filter(cat => !usedCategories.includes(cat));
    setAllowedCategories(otherCategories);
    setNewActivityModalOpen(true);
  };

  const handleAddNote = () => {
    setActiveLeftTab('notes');
    setFocusNotesEditor(true);
  };

  const handleCreateContact = () => {
    setContactModalOpen(true);
  };

  const handleNotesEditorFocused = () => {
    setFocusNotesEditor(false);
  };

  const handleUploadDocument = () => {
    setNewDocModalOpen(true);
  };

  const handleNewTextDocCommit = async (data: TextDocData) => {
    try {
      const user_uid = getUserUid();
      
      if (!user_uid) {

        return;
      }

      if (!boardId) {

        return;
      }

      if (!data.title?.trim()) {

        return;
      }

      const documentPayload = {
        user_uid,
        type: 'text' as const,
        name: data.title.trim(),
        doc_category: data.category || 'Other',
        description: data.description?.trim() || null,
        text_value: data.content?.trim() || null,
        first_name: data.firstName?.trim() || null,
        last_name: data.lastName?.trim() || null,
        email: data.email?.trim() || null,
        phone_number: data.phone?.trim() || null,
        kanban_uid: boardId,
        job_uid: jobId || null,
      };

      await api.post(API_ENDPOINTS.documents.create, documentPayload);
      
      setNewTextDocModalOpen(false);
      setNewDocModalOpen(false);
    } catch (error: any) {

    }
  };

  const handleNewPdfDocCommit = async (data: NewPdfDocData) => {
    try {
      const user_uid = getUserUid();
      
      if (!user_uid) {

        return;
      }

      if (!boardId) {

        return;
      }

      if (!data.file) {

        return;
      }

      if (!data.title?.trim()) {

        return;
      }

      const formData = new FormData();
      formData.append('user_uid', user_uid);
      formData.append('type', 'doc');
      formData.append('name', data.title.trim());
      formData.append('doc_category', data.category || 'Other');
      
      if (data.description?.trim()) {
        formData.append('description', data.description.trim());
      }
      
      formData.append('kanban_uid', boardId);
      
      if (jobId) {
        formData.append('job_uid', jobId);
      }
      
      formData.append('file', data.file);

      await api.postFormData(API_ENDPOINTS.documents.upload, formData);
      
      setNewPdfDocModalOpen(false);
      setNewDocModalOpen(false);
    } catch (error: any) {

    }
  };

  const handleSubmitContact = async (data: Contact) => {
    try {
      const user_uid = getUserUid();
      if (!user_uid) {

        return;
      }

      if (!data.firstName?.trim()) {

        return;
      }
      
      if (!data.lastName?.trim()) {

        return;
      }

      const contactPayload = {
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        job_title: data.title?.trim() || null,
        companies_name: data.company?.trim() ? [data.company.trim()] : null,
        email: data.email?.trim() ? [data.email.trim()] : null,
        phone: data.phone?.trim() ? [data.phone.trim()] : null,
        linkedin_url: data.linkedin?.trim() || null,
        twitter_url: data.twitter?.trim() || null,
        meta_url: data.facebook?.trim() || null,
        github_url: data.github?.trim() || null,
        board_linked: data.boardLinked || boardId || null,
        job_linked: data.jobLinked || jobId || null,
      };

      await api.post(API_ENDPOINTS.contacts.create, contactPayload);
      
      setContactModalOpen(false);
    } catch (error) {

    }
  };

  const handleCreateActivity = async (data: ActivityData) => {
    try {
      const user_uid = getUserUid();
      
      if (!user_uid) {

        return;
      }

      if (!data.board) {

        return;
      }

      if (!boardId) {

        return;
      }

      if (!data.title?.trim()) {

        return;
      }

      const activityPayload = {
        title: data.title.trim(),
        type: data.category as any,
        description: data.note?.trim() || null,
        is_completed: data.completed || false,
        start_date: data.startDate ? new Date(data.startDate).toISOString() : null,
        end_date: data.endDate ? new Date(data.endDate).toISOString() : null,
        board_uid: data.board || boardId,
        job_uid: data.job || jobId || null,
        contact_uid: null,
        status: "active" as const,
      };

      await api.post(API_ENDPOINTS.activities.create, activityPayload);
      
      setNewActivityModalOpen(false);
      setAllowedCategories(undefined);
    } catch (error: any) {

      
      if (error?.status === 422) {

      } else if (error?.status === 401) {

      } else {

      }
    }
  };

  if (!open) return null;

  return (
    <div className="tdm-overlay">
      <div className="tdm-overlay-bg" onClick={handleClose} />
      <div className="tdm-modal">
        <div className="tdm-grid">
          <div className="tdm-left">
            <div className="tdm-header">
              <div className="ejm-job-info">
                <div className="ejm-job-title">{jobData?.job_title || jobTitle}</div>
                <div className="ejm-company-name">{jobData?.company_name || companyName}</div>
              </div>
              <div className="tdm-header-actions">
                <div className="ejm-move-wrapper" ref={moveWrapperRef}>
                  <button
                    className="ejm-move-btn"
                    type="button"
                    onClick={() => setShowMoveDropdown((open) => !open)}
                    aria-haspopup="menu"
                    aria-expanded={showMoveDropdown}
                  >
                    Move
                  </button>
                  {showMoveDropdown && availableLanes.length > 0 && (
                    <div className="ejm-move-dropdown">
                      {availableLanes.map((lane) => (
                        <button
                          key={lane.id}
                          className={`ejm-move-option ${lane.id === currentLane ? 'disabled' : ''}`}
                          onClick={() => handleMoveToLane(lane.id)}
                          disabled={lane.id === currentLane}
                          type="button"
                        >
                          <span className="ejm-move-icon">
                            {renderLaneIcon(lane.icon, lane.title)}
                          </span>
                          <span className="ejm-move-text">{lane.title}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  className="tdm-btn"
                  onClick={handleClose}
                >
                  Close
                </button>
              </div>
            </div>
            <div className="tdm-divider" />
            
            <div className="ejm-left-tabs">
              <button
                className={`ejm-left-tab ${activeLeftTab === 'edit' ? 'active' : ''}`}
                onClick={() => setActiveLeftTab('edit')}
              >
                <span className="ejm-left-tab-icon">
                  <Pencil size={14} />
                </span>
                Edit
              </button>
              <button
                className={`ejm-left-tab ${activeLeftTab === 'ai-tools' ? 'active' : ''}`}
                onClick={() => setActiveLeftTab('ai-tools')}
              >
                <span className="ejm-left-tab-icon">
                  <Sparkles size={14} />
                </span>
                AI Tools
              </button>
              <button
                className={`ejm-left-tab ${activeLeftTab === 'activities' ? 'active' : ''}`}
                onClick={() => setActiveLeftTab('activities')}
              >
                <span className="ejm-left-tab-icon">
                  <Activity size={14} />
                </span>
                Activities
              </button>
              <button
                className={`ejm-left-tab ${activeLeftTab === 'notes' ? 'active' : ''}`}
                onClick={() => setActiveLeftTab('notes')}
              >
                <span className="ejm-left-tab-icon">
                  <NotebookPen size={14} />
                </span>
                Notes
              </button>
              <button
                className={`ejm-left-tab ${activeLeftTab === 'contacts' ? 'active' : ''}`}
                onClick={() => setActiveLeftTab('contacts')}
              >
                <span className="ejm-left-tab-icon">
                  <ContactIcon size={14} />
                </span>
                Contacts
              </button>
              <button
                className={`ejm-left-tab ${activeLeftTab === 'documents' ? 'active' : ''}`}
                onClick={() => setActiveLeftTab('documents')}
              >
                <span className="ejm-left-tab-icon">
                  <Files size={14} />
                </span>
                Documents
              </button>
            </div>

            <div className="ejm-left-content">
              {activeLeftTab === 'edit' && (
                loadingJobData && !jobData ? (
                  <div className="edit-tab-loader">
                    <div className="edit-tab-spinner" />
                  </div>
                ) : (
                  <EditTab jobData={jobData} jobId={jobId} boardId={boardId} defaultColor={DEFAULT_COLOR} onJobUpdated={fetchJobData} onColorChange={(color) => jobId && onColorChange?.(jobId, color)} />
                )
              )}
              {activeLeftTab === 'ai-tools' && <AIToolsTab jobId={jobId} />}
              {activeLeftTab === 'activities' && <ActivitiesTab jobId={jobId} boardId={boardId} jobTitle={jobTitle} companyName={companyName} />}
              {activeLeftTab === 'notes' && <NotesTab jobId={jobId} focusEditor={focusNotesEditor} onEditorFocused={handleNotesEditorFocused} highlightEntityUid={highlightEntityUid} />}
              {activeLeftTab === 'contacts' && <ContactsTab jobId={jobId} boardId={boardId} highlightEntityUid={highlightEntityUid} />}
              <div style={{ display: activeLeftTab === 'documents' ? undefined : 'none' }}>
                <DocumentsTab jobId={jobId} boardId={boardId} highlightEntityUid={highlightEntityUid} />
              </div>
            </div>
          </div>

          <div className="tdm-right">
            <div className="ejm-tabs">
              <button
                className={`ejm-tab ${activeTab === 'shortcuts' ? 'active' : ''}`}
                onClick={() => setActiveTab('shortcuts')}
              >
                Shortcuts
              </button>
              <button
                className={`ejm-tab ${activeTab === 'timeline' ? 'active' : ''}`}
                onClick={() => setActiveTab('timeline')}
              >
                Timeline
              </button>
            </div>

            {activeTab === 'shortcuts' && (
              <div className="ejm-shortcuts">
                    <div className="ejm-section">
                      <h3 className="ejm-section-title">Application Materials</h3>
                      <button className="ejm-shortcut-btn">
                        <span className="ejm-icon">+</span>
                        Create Tailored Resume
                      </button>
                      <button className="ejm-shortcut-btn">
                        <span className="ejm-icon">+</span>
                        Generate Cover Letter
                      </button>
                      <button className="ejm-shortcut-btn" onClick={handleUploadDocument} type="button">
                        <span className="ejm-icon">+</span>
                        Upload Document
                      </button>
                    </div>

                <div className="ejm-section">
                  <h3 className="ejm-section-title">Activity Logging</h3>
                  <button className="ejm-shortcut-btn" onClick={handleLogApplication} type="button">
                    <span className="ejm-icon">+</span>
                    Log Application
                  </button>
                  <button className="ejm-shortcut-btn" onClick={handleLogInterview} type="button">
                    <span className="ejm-icon">+</span>
                    Log Interview
                  </button>
                  <button className="ejm-shortcut-btn" onClick={handleLogOfferReceived} type="button">
                    <span className="ejm-icon">+</span>
                    Log Offer Received
                  </button>
                  <button className="ejm-shortcut-btn" onClick={handleLogOfferAccepted} type="button">
                    <span className="ejm-icon">+</span>
                    Log Offer Accepted
                  </button>
                  <button className="ejm-shortcut-btn" onClick={handleLogOtherActivity} type="button">
                    <span className="ejm-icon">+</span>
                    Log Other Activity
                  </button>
                </div>

                <div className="ejm-section">
                  <h3 className="ejm-section-title">Other</h3>
                  <button className="ejm-shortcut-btn" onClick={handleAddNote} type="button">
                    <span className="ejm-icon">+</span>
                    Add Note
                  </button>
                  <button className="ejm-shortcut-btn" onClick={handleCreateContact} type="button">
                    <span className="ejm-icon">+</span>
                    Create Contact
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'timeline' && (
              <TimelineTab jobId={jobId} onNavigateTo={handleTimelineNavigate} />
            )}
          </div>
        </div>
      </div>

      <NewActivityModal
        open={newActivityModalOpen}
        onClose={() => {
          setNewActivityModalOpen(false);
          setAllowedCategories(undefined);
        }}
        onCommit={handleCreateActivity}
        currentBoard={boardId || undefined}
        currentJob={jobId || undefined}
        allowedCategories={allowedCategories}
      />

      <ContactsModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        initial={{
          boardLinked: boardId || undefined,
          jobLinked: jobId || undefined,
        }}
        onSubmit={handleSubmitContact}
        boardId={boardId || undefined}
      />

      <NewDocumentModal
        open={newDocModalOpen}
        onClose={() => setNewDocModalOpen(false)}
        title="Create New Document"
        size="md"
      >
        <p style={{ margin: '0 0 12px 0', color: '#6b7280', fontSize: '14px', textAlign: 'left' }}>
          Choose the type of document you want to create:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            className="modal__btn"
            style={{ textAlign: 'left', padding: '12px 16px', height: 'auto' }}
            onClick={() => {
              setNewDocModalOpen(false);
              setNewTextDocModalOpen(true);
            }}
            type="button"
          >
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Create Text Document</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Create a new text document using the inline text editor.</div>
          </button>
          <button
            className="modal__btn"
            style={{ textAlign: 'left', padding: '12px 16px', height: 'auto' }}
            onClick={() => {
              setNewDocModalOpen(false);
              setNewPdfDocModalOpen(true);
            }}
            type="button"
          >
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Upload File</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Upload an existing file from your computer. Allowed file types: .pdf, .doc, .docx, .png, .jpg</div>
          </button>
        </div>
      </NewDocumentModal>

      <NewTextDocumentModal
        open={newTextDocModalOpen}
        savedValue={{
          title: '',
          category: 'Resume',
          description: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          content: '',
          boardLinked: boardId || undefined,
          jobLinked: jobId || undefined,
        }}
        onClose={() => setNewTextDocModalOpen(false)}
        onCommit={handleNewTextDocCommit}
      />

      <NewPDFDocumentModal
        open={newPdfDocModalOpen}
        savedValue={{
          title: '',
          category: 'Resume',
          description: '',
          boardLinked: boardId || undefined,
          jobLinked: jobId || undefined,
        }}
        onClose={() => setNewPdfDocModalOpen(false)}
        onCommit={handleNewPdfDocCommit}
      />
    </div>
  );
}

